import { Component, inject, OnInit } from '@angular/core';
import { EpTableComponent, EPTableConfig, isNotNullOrUndefined } from '@ep/shared';
import { AsyncPipe, formatDate } from '@angular/common';
import { ReportsApiService } from '../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { SettlementReportItem } from '@app/resources/services';
import { map, Observable, of, switchMap, tap, catchError } from 'rxjs';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { mapToBatchDetail } from '../../utils';
import { BreadcrumbService } from '@app/resources/services/breadcrumb.service';
import { FA_ICON } from '@shared/icons';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-existing-batch-report-detail',
  standalone: true,
  imports: [EpTableComponent, AsyncPipe],
  template: `
    <div class="p-4">
      <app-ep-table
        [config]="config"
        [data]="batchDetail$ | async"
        (dateChange)="dateChange($event)"
        data-cy="batches-detail-table"
      />
    </div>
  `,
  styleUrl: './existing-batch-report-detail.component.scss',
})
export class ExistingBatchReportDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly reportService = inject(ReportsApiService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  protected batchId: string | undefined = this.route.snapshot.url.map((segment) => segment.path).pop();
  protected displayBatchId: number | undefined;
  protected displayBatchDate: Date | null = null;

  protected batchDetail$: Observable<SettlementReportItem[]> = of([]);

  protected config: EPTableConfig<SettlementReportItem> = {
    header: 'Batch Details',
    columns: [
      {
        header: 'Label',
        field: 'Label',
        columnType: 'string',
        defaultValue: null,
      },
      {
        header: 'Quantity',
        field: 'Count',
        columnType: 'string',
        defaultValue: '0',
      },
      {
        header: 'Amount',
        field: 'Amount',
        columnType: 'string',
        defaultValue: null,
      },
    ],
    calendarFilter: true,
    calendarDate: this.router.parseUrl(this.router.url).queryParams['date']
      ? new Date(`${this.router.parseUrl(this.router.url).queryParams['date'] + 'T00:00:00'}`)
      : new Date(),
    style: {},
    globalFilterFields: ['Label'],
  };

  public ngOnInit(): void {
    this.updatePageBreadcrumbs();

    this.batchDetail$ = this.reportService.searchForBatchesById(Number(this.batchId)).pipe(
      tap((batch) => {
        this.displayBatchId = batch.batchId;
        this.displayBatchDate = batch.closeDate;
        this.updatePageBreadcrumbs();
      }),
      map(mapToBatchDetail)
    );
  }

  private updatePageBreadcrumbs(): void {
    const batchDate = this.displayBatchDate ? formatDate(this.displayBatchDate, 'yyyy-MM-dd', 'en-US') : '';

    const breadcrumbs: MenuItem[] = [
      { label: 'Reports', icon: FA_ICON.FILE_EXPORT, command: () => this.navigateBackToReports() },
      { label: `Batch Reports ${batchDate}`.trim(), command: () => this.navigateBack() },
    ];

    if (isNotNullOrUndefined(this.displayBatchId)) {
      breadcrumbs.push({ label: `Batch Id ${this.displayBatchId}` });
    }

    this.breadcrumbService.setPageBreadcrumbs(breadcrumbs);
  }

  protected location$: Observable<number> = this.applicationProfileService.applicationProfile$.pipe(
    switchMap((appProfile) => {
      return of(appProfile.Location.LocationId);
    })
  );

  public navigateBack(): void {
    this.location$.subscribe((location) => {
      const formattedDate = this.displayBatchDate
        ? new Date(this.displayBatchDate + 'Z').toISOString().split('T')[0]
        : undefined;

      this.router.navigate([`${location}/reports/BatchReport`], { queryParams: { date: formattedDate } });
    });
  }

  public navigateBackToReports(): void {
    this.location$.subscribe((location) => {
      this.router.navigate([`${location}/reports`]);
    });
  }

  public dateChange(date: Date): void {
    this.batchDetail$ = this.reportService.searchForBatchesByDate(date).pipe(
      switchMap((batches) => {
        if (batches.length === 0) {
          return of([]);
        }

        if (batches.length === 1) {
          return this.reportService.searchForBatchesById(Number(batches[0].BatchId)).pipe(
            tap((batch) => {
              this.displayBatchId = batch.batchId;
              this.displayBatchDate = batch.closeDate;
              this.updatePageBreadcrumbs();
            }),
            map(mapToBatchDetail)
          );
        }

        // Multiple batches found, navigate to batch list
        this.displayBatchDate = batches[0].ClosedDateTime;
        this.navigateBack();
        return of([]);
      }),
      catchError((error) => {
        console.error('Error fetching batch data:', error);
        return of([]);
      })
    );
  }
}
